import { UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import { toolTempUploadKey } from '@/app/hooks';
import type {
  FormAudioPrompt,
  FormSubtitlePrompt,
  FormVideoPrompt,
  SourceAudio,
  SourceSubtitle,
  SourceVideo,
  VideoPrompt,
} from '@/app/types';
import { type UploadResult, uploadFile } from '@/app/utils/client';
import type { TempUploadItem, TempUploadRefs, TempUploadReplacement } from '../../types';
import type { FormPromptListHelpers } from '../types';

/**
 * 图片补传的实现已上移到 create/_utils/ —— img2video、reference 等非 tools 表单也在复用。
 * 此处保留原名转发，避免改动十余个 tool form 的 import。
 */
export {
  buildUploadedImageFields,
  getTempImagesPendingItems as getToolTempImagesPendingItems,
  getTempSourceImageItems as getToolTempSourceImageItems,
} from '../../_utils/temp-image-upload-items';

type TempVideoPrompt = VideoPrompt & {
  _tempFile?: File;
  _tempCoverImage?: Blob;
  width?: number;
  height?: number;
};

function isKeyScheduled(refs: TempUploadRefs, key: string): boolean {
  return refs.processed.current.has(key) || refs.pending.current.has(key);
}

function getUriReplacements(...pairs: Array<{ from?: string; to?: string }>): TempUploadReplacement[] {
  return pairs.filter(({ from, to }) => !!from && !!to && from !== to) as TempUploadReplacement[];
}

async function uploadVideoFileWithCover(
  file: File,
  options: { width: number; height: number; duration?: number },
  coverImage?: Blob,
  scene: UploadScene = UPLOAD_SCENE.VIDU,
) {
  return Promise.all([
    uploadFile({ file, ...options, scene }),
    coverImage ? uploadFile({ file: coverImage, scene }) : Promise.resolve(null),
  ]);
}

export function getToolTempAudioPendingItems(
  refs: TempUploadRefs,
  helpers: FormPromptListHelpers<FormAudioPrompt>,
  ctx: { audioPrompts: Array<FormAudioPrompt | null>; onUploadError?: (error: UploadResult['error']) => void },
): TempUploadItem[] {
  const { updatePromptItem, markPromptStatus } = helpers;
  const pendingUploads = ctx.audioPrompts
    .map((prompt) => {
      if (!prompt || !prompt.prompt?._tempFile) return null;
      const uploadKey = toolTempUploadKey('audio', prompt.id);
      if (isKeyScheduled(refs, uploadKey)) return null;
      return { prompt, uploadKey };
    })
    .filter((item): item is { prompt: FormAudioPrompt; uploadKey: string } => item !== null);

  return pendingUploads.map(({ prompt, uploadKey }) => ({
    uploadKey,
    run: async (): Promise<TempUploadReplacement[]> => {
      markPromptStatus(prompt.id, 'uploading');
      const currentPrompt = prompt.prompt;
      if (!currentPrompt?._tempFile) {
        markPromptStatus(prompt.id, 'done');
        return [];
      }
      const uploadResult = await uploadFile({
        file: currentPrompt._tempFile,
        duration: currentPrompt.duration,
        scene: UPLOAD_SCENE.VIDU,
      });
      if (uploadResult.error || !uploadResult.uri) {
        console.error('Failed to upload temp audio:', prompt.id, uploadResult.error);
        ctx.onUploadError?.(uploadResult.error);
        markPromptStatus(prompt.id, 'done');
        throw new Error(String(uploadResult.error));
      }
      updatePromptItem(prompt.id, (latestPrompt) => {
        if (!latestPrompt.prompt) return latestPrompt;
        const latestAudioPrompt = latestPrompt.prompt as SourceAudio;
        const { _tempFile, ...promptWithoutTemp } = latestAudioPrompt;
        return {
          ...latestPrompt,
          status: 'done',
          prompt: {
            ...promptWithoutTemp,
            id: uploadResult.id ?? promptWithoutTemp.id,
            src: uploadResult.uri,
            preview: promptWithoutTemp.preview || uploadResult.uri,
          },
        };
      });
      return getUriReplacements({ from: currentPrompt.src, to: uploadResult.uri });
    },
  }));
}

export function getToolTempSubtitlePendingItems(
  refs: TempUploadRefs,
  helpers: FormPromptListHelpers<FormSubtitlePrompt>,
  ctx: { subtitlePrompts: FormSubtitlePrompt[] },
): TempUploadItem[] {
  const { updatePromptItem, markPromptStatus } = helpers;
  const pendingUploads = ctx.subtitlePrompts
    .map((prompt) => {
      if (!prompt || !prompt.prompt?._tempFile) return null;
      const uploadKey = toolTempUploadKey('subtitle', prompt.id);
      if (isKeyScheduled(refs, uploadKey)) return null;
      return { prompt, uploadKey };
    })
    .filter((item): item is { prompt: FormSubtitlePrompt; uploadKey: string } => item !== null);

  return pendingUploads.map(({ prompt, uploadKey }) => ({
    uploadKey,
    run: async (): Promise<TempUploadReplacement[]> => {
      markPromptStatus(prompt.id, 'uploading');
      const currentPrompt = prompt.prompt;
      if (!currentPrompt?._tempFile) {
        markPromptStatus(prompt.id, 'done');
        return [];
      }
      const uploadResult = await uploadFile({
        file: currentPrompt._tempFile,
        width: 0,
        height: 0,
        scene: UPLOAD_SCENE.VIDU,
      });
      if (uploadResult.error || !uploadResult.uri) {
        console.error('Failed to upload temp subtitle:', prompt.id, uploadResult.error);
        markPromptStatus(prompt.id, 'done');
        throw new Error(String(uploadResult.error));
      }
      updatePromptItem(prompt.id, (latestPrompt) => {
        if (!latestPrompt.prompt) return latestPrompt;
        const latestSubtitlePrompt = latestPrompt.prompt as SourceSubtitle;
        const { _tempFile, ...promptWithoutTemp } = latestSubtitlePrompt;
        return {
          ...latestPrompt,
          status: 'done',
          prompt: { ...promptWithoutTemp, src: uploadResult.uri },
        };
      });
      return getUriReplacements({ from: currentPrompt.src, to: uploadResult.uri });
    },
  }));
}

export function getToolTempVideosPendingItems(
  refs: TempUploadRefs,
  helpers: FormPromptListHelpers<FormVideoPrompt>,
  ctx: { videoPrompts: Array<FormVideoPrompt | null> },
  scene: UploadScene = UPLOAD_SCENE.VIDU,
): TempUploadItem[] {
  const { updatePromptItem, markPromptStatus } = helpers;
  const pendingUploads = ctx.videoPrompts
    .map((prompt) => {
      const currentPrompt = prompt?.prompt as TempVideoPrompt | null;
      if (!prompt || !currentPrompt?._tempFile) return null;
      const uploadKey = toolTempUploadKey('video', prompt.id);
      if (isKeyScheduled(refs, uploadKey)) return null;
      return { prompt, uploadKey };
    })
    .filter((item): item is { prompt: FormVideoPrompt; uploadKey: string } => item !== null);

  return pendingUploads.map(({ prompt, uploadKey }) => ({
    uploadKey,
    run: async (): Promise<TempUploadReplacement[]> => {
      markPromptStatus(prompt.id, 'uploading');
      const currentPrompt = prompt.prompt as TempVideoPrompt | null;
      if (!currentPrompt?._tempFile) {
        markPromptStatus(prompt.id, 'done');
        return [];
      }
      const [videoUploadResult, coverUploadResult] = await uploadVideoFileWithCover(
        currentPrompt._tempFile,
        { width: currentPrompt.width ?? 0, height: currentPrompt.height ?? 0, duration: currentPrompt.duration },
        currentPrompt._tempCoverImage,
        scene,
      );
      if (videoUploadResult.error || !videoUploadResult.uri) {
        console.error('Failed to upload temp video:', prompt.id, videoUploadResult.error);
        markPromptStatus(prompt.id, 'done');
        throw new Error(String(videoUploadResult.error));
      }
      updatePromptItem(prompt.id, (latestPrompt) => {
        if (!latestPrompt.prompt) return latestPrompt;
        const latestVideoPrompt = latestPrompt.prompt as TempVideoPrompt;
        const { _tempFile, _tempCoverImage, ...promptWithoutTemp } = latestVideoPrompt;
        return {
          ...latestPrompt,
          status: 'done',
          prompt: {
            ...promptWithoutTemp,
            videoId: videoUploadResult.id ?? promptWithoutTemp.videoId,
            video: videoUploadResult.uri,
            videoPreview: promptWithoutTemp.videoPreview || videoUploadResult.uri,
            coverImage:
              !coverUploadResult || coverUploadResult.error || !coverUploadResult.uri
                ? promptWithoutTemp.coverImage
                : coverUploadResult.uri,
            coverImagePreview:
              promptWithoutTemp.coverImagePreview || promptWithoutTemp.coverImage || promptWithoutTemp.videoPreview,
          },
        };
      });
      return getUriReplacements(
        { from: currentPrompt.video, to: videoUploadResult.uri },
        { from: currentPrompt.coverImage, to: coverUploadResult?.uri },
      );
    },
  }));
}

export function getToolTempSourceVideoItems(
  refs: TempUploadRefs,
  ctx: {
    sourceVideo: SourceVideo | null;
    getSourceVideo: () => SourceVideo | null;
    setSourceVideo: (video: SourceVideo | null) => void;
    onStatusChange?: (status: 'uploading' | 'done') => void;
  },
): TempUploadItem[] {
  const { sourceVideo, getSourceVideo, setSourceVideo, onStatusChange } = ctx;
  if (!sourceVideo?._tempFile) return [];
  const uploadKey = toolTempUploadKey(
    'source-video',
    String(sourceVideo.tempId || sourceVideo.id || sourceVideo.preview),
  );
  if (isKeyScheduled(refs, uploadKey)) return [];

  return [
    {
      uploadKey,
      run: async (): Promise<TempUploadReplacement[]> => {
        onStatusChange?.('uploading');
        try {
          const currentSourceVideo = getSourceVideo();
          if (!currentSourceVideo?._tempFile) {
            return [];
          }
          const [videoUploadResult, coverUploadResult] = await uploadVideoFileWithCover(
            currentSourceVideo._tempFile,
            {
              width: currentSourceVideo.width ?? 0,
              height: currentSourceVideo.height ?? 0,
              duration: currentSourceVideo.duration,
            },
            currentSourceVideo._tempCoverImage,
          );
          if (videoUploadResult.error || !videoUploadResult.uri) {
            console.error('Failed to upload temp source video:', videoUploadResult.error);
            throw new Error(String(videoUploadResult.error));
          }
          const latestSourceVideo = getSourceVideo();
          if (!latestSourceVideo) {
            return [];
          }
          const { _tempFile, _tempCoverImage, ...videoWithoutTemp } = latestSourceVideo;
          setSourceVideo({
            ...videoWithoutTemp,
            id: videoUploadResult.id ?? videoWithoutTemp.id,
            src: videoUploadResult.uri,
            preview: videoWithoutTemp.preview || videoUploadResult.uri,
            cover_url:
              !coverUploadResult || coverUploadResult.error || !coverUploadResult.uri
                ? videoWithoutTemp.cover_url
                : coverUploadResult.uri,
            cover_preview_url:
              videoWithoutTemp.cover_preview_url || videoWithoutTemp.cover_url || videoWithoutTemp.preview,
          });
          return getUriReplacements(
            { from: currentSourceVideo.src, to: videoUploadResult.uri },
            { from: currentSourceVideo.cover_url, to: coverUploadResult?.uri },
          );
        } finally {
          onStatusChange?.('done');
        }
      },
    },
  ];
}
